/**
 * Renders markdown content as read-only HTML.
 * Processes [[hex-name]] wiki-style links to create clickable hex references.
 * Supports multiple themes: 'dark' (default) and 'parchment' (for missives).
 */

import MDEditor from '@uiw/react-md-editor';

/** Theme configuration for different rendering contexts */
type EditorTheme = 'dark' | 'light' | 'parchment';

interface Props {
    /** Markdown content string */
    content: string | null;
    /** Map of hex names to their IDs for resolving [[hex-name]] links */
    hexNameToId?: Record<string, number>;
    /** Theme for styling - 'dark' for standard pages, 'light' for light backgrounds, 'parchment' for missives */
    theme?: EditorTheme;
}

/** Theme-specific style configurations */
const themeStyles: Record<EditorTheme, { color: string; linkClass: string; emptyClass: string }> = {
    dark: {
        color: '#e5e7eb', // gray-200 - light text for dark backgrounds
        linkClass: 'text-purple-400 hover:text-purple-300 transition-colors',
        emptyClass: 'text-gray-300 italic',
    },
    light: {
        color: '#111827', // gray-900 - dark text for light backgrounds
        linkClass: 'text-blue-600 hover:text-blue-800 underline transition-colors',
        emptyClass: 'text-gray-500 italic',
    },
    parchment: {
        color: '#000000', // black for parchment background
        linkClass: 'text-amber-800 hover:text-amber-600 underline transition-colors',
        emptyClass: 'text-amber-800/60 italic',
    },
};

/**
 * Process markdown text to convert [[hex-name]] syntax to markdown links.
 * This runs before the markdown is rendered.
 */
function processHexLinks(markdown: string, hexNameToId: Record<string, number>): string {
    return markdown.replace(/\[\[([^\]]+)\]\]/g, (match, hexName) => {
        const hexId = hexNameToId[hexName];
        if (hexId) {
            // Convert to standard markdown link
            return `[${hexName}](/hex/${hexId})`;
        }
        // If hex not found, show as styled text (will be rendered as-is)
        return `~~${hexName}~~`;
    });
}

/**
 * Renders markdown content as HTML for read-only display.
 * Processes hex links in the format [[hex-name]] and converts them to clickable links.
 */
export function EditorRenderer({ content, hexNameToId = {}, theme = 'dark' }: Props) {
    const styles = themeStyles[theme];

    if (!content || content.trim() === '') {
        return <p className={styles.emptyClass}>No content yet.</p>;
    }

    // Process hex links before rendering
    const processedContent = processHexLinks(content, hexNameToId);

    // Use light color mode for light/parchment themes, dark for default
    const colorMode = theme === 'dark' ? 'dark' : 'light';

    return (
        <div data-color-mode={colorMode} className="hex-content">
            <MDEditor.Markdown
                source={processedContent}
                style={{
                    backgroundColor: 'transparent',
                    color: styles.color,
                }}
                // Custom link styling for hex links
                components={{
                    a: ({ children, href, ...props }) => (
                        <a
                            href={href}
                            className={styles.linkClass}
                            {...props}
                        >
                            {children}
                        </a>
                    ),
                }}
            />
        </div>
    );
}

export default EditorRenderer;

